import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowClockwise, ArrowLeft } from 'shared/icons';
import PageHeader from '../components/layout/PageHeader';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import ErrorCard from '../components/ui/ErrorCard';
import StandardTable from '../components/ui/StandardTable';
import api from '../services/api';
import { AggregateBy, ExtractPaging, ExtractResults } from '../utils/aggregation';
import { FormatDateTimeBr } from '../utils/date';
import { FormatPrice } from '../utils/format';

const DEFAULT_LIMIT = 15;

function formatOrderItemsSummary(order) {
    const items = Array.isArray(order.order_items) ? order.order_items : Array.isArray(order.items) ? order.items : [];
    if (items.length === 0) {
        return '-';
    }

    const grouped = AggregateBy(items, (item) => item.item_title || item.seller_sku || item.item_id || 'Produto', {
        quantity: (acc, item) => {
            const quantity = Number(item.quantity || 0);
            return (acc || 0) + (Number.isFinite(quantity) ? quantity : 0);
        },
    });

    return [...grouped.entries()].map(([label, { quantity }]) => `${label} x${quantity}`).join(', ');
}

const Customers = () => {
    const navigate = useNavigate();
    const { buyerId } = useParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [orders, setOrders] = useState([]);
    const [customersPaging, setCustomersPaging] = useState({ total: 0, limit: DEFAULT_LIMIT, offset: 0 });
    const [ordersPaging, setOrdersPaging] = useState({ total: 0, limit: DEFAULT_LIMIT, offset: 0 });
    const [customersPage, setCustomersPage] = useState(1);
    const [customersPageSize, setCustomersPageSize] = useState(DEFAULT_LIMIT);
    const [ordersPage, setOrdersPage] = useState(1);
    const [ordersPageSize, setOrdersPageSize] = useState(DEFAULT_LIMIT);
    const [customersSearchInput, setCustomersSearchInput] = useState('');
    const [customersSearch, setCustomersSearch] = useState('');
    const [customersSortState, setCustomersSortState] = useState({
        columnKey: 'buyer_id',
        direction: 'desc',
    });

    const isDetailsMode = Boolean(buyerId);
    const customersOffset = Math.max(0, (customersPage - 1) * customersPageSize);
    const ordersOffset = Math.max(0, (ordersPage - 1) * ordersPageSize);

    const loadCustomers = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const sortBy =
                customersSortState?.columnKey && customersSortState?.direction !== 'none'
                    ? customersSortState.columnKey
                    : undefined;
            const sortDirection =
                customersSortState?.direction === 'asc' || customersSortState?.direction === 'desc'
                    ? customersSortState.direction
                    : undefined;
            const query = {
                limit: customersPageSize,
                offset: customersOffset,
            };
            if (customersSearch) {
                query.search = customersSearch;
            }
            if (sortBy) {
                query.sort_by = sortBy;
            }
            if (sortDirection) {
                query.sort_direction = sortDirection;
            }

            const data = await api.getCustomers(query);
            const rows = ExtractResults(data, ['results', 'customers']);
            setCustomers(rows);
            setCustomersPaging(
                ExtractPaging(data, { total: rows.length, limit: customersPageSize, offset: customersOffset }),
            );
        } catch (loadError) {
            setError(loadError.message || 'Erro ao carregar clientes');
        } finally {
            setLoading(false);
        }
    }, [customersPageSize, customersOffset, customersSearch, customersSortState]);

    const loadCustomerDetails = useCallback(async () => {
        if (!buyerId) {
            return;
        }

        try {
            setLoading(true);
            setError('');

            const data = await api.getCustomer(buyerId, {
                limit: ordersPageSize,
                offset: ordersOffset,
            });
            setSelectedCustomer(data.customer || null);
            setOrders(Array.isArray(data.orders) ? data.orders : []);
            setOrdersPaging({
                total: Number(data?.paging?.total || 0),
                limit: Number(data?.paging?.limit || ordersPageSize),
                offset: Number(data?.paging?.offset || ordersOffset),
            });
        } catch (loadError) {
            setError(loadError.message || 'Erro ao carregar cliente');
            setSelectedCustomer(null);
            setOrders([]);
            setOrdersPaging({ total: 0, limit: ordersPageSize, offset: ordersOffset });
        } finally {
            setLoading(false);
        }
    }, [buyerId, ordersPageSize, ordersOffset]);

    useEffect(() => {
        if (isDetailsMode) {
            loadCustomerDetails();
            return;
        }

        loadCustomers();
    }, [isDetailsMode, loadCustomers, loadCustomerDetails]);

    useEffect(() => {
        setOrdersPage(1);
        setOrdersPageSize(DEFAULT_LIMIT);
    }, [buyerId]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setCustomersPage(1);
            setCustomersSearch(customersSearchInput.trim());
        }, 350);

        return () => clearTimeout(timer);
    }, [customersSearchInput]);

    const customersWithFallbackName = useMemo(() => {
        return customers.map((customer) => ({
            ...customer,
            display_name: customer.display_name || customer.buyer_nickname || `Cliente ${customer.buyer_id}`,
        }));
    }, [customers]);

    const customerColumns = useMemo(
        () => [
            {
                key: 'display_name',
                label: 'Cliente',
                getValue: (customer) => customer.display_name,
                render: (customer) => (
                    <Link
                        to={`/customers/${customer.buyer_id}`}
                        className="font-medium text-blue-600 hover:underline dark:text-blue-400"
                    >
                        {customer.display_name}
                    </Link>
                ),
            },
            {
                key: 'buyer_id',
                label: 'ID',
                getValue: (customer) => customer.buyer_id,
            },
            {
                key: 'total_orders',
                label: 'Pedidos',
                getValue: (customer) => customer.total_orders,
            },
            {
                key: 'total_spent',
                label: 'Total gasto',
                getValue: (customer) => customer.total_spent,
                render: (customer) => (
                    <span className="text-black dark:text-white">{FormatPrice(customer.total_spent)}</span>
                ),
            },
            {
                key: 'last_order_date',
                label: 'Ultima compra',
                getValue: (customer) => customer.last_order_date,
                render: (customer) => FormatDateTimeBr(customer.last_order_date, { blank: '-' }),
            },
        ],
        [],
    );

    const customerOrderColumns = useMemo(
        () => [
            {
                key: 'ml_order_id',
                label: 'Pedido',
                getValue: (order) => order.ml_order_id || order.external_order_id || order.order_id || '-',
                render: (order) => (
                    <span className="font-medium text-black dark:text-white">
                        {order.ml_order_id || order.external_order_id || order.order_id || '-'}
                    </span>
                ),
            },
            {
                key: 'date_created',
                label: 'Data',
                getValue: (order) => order.date_created,
                render: (order) => FormatDateTimeBr(order.date_created, { blank: '-' }),
            },
            {
                key: 'status',
                label: 'Status',
                getValue: (order) => order.status,
                render: (order) => <Badge variant="secondary">{order.status || '-'}</Badge>,
            },
            {
                key: 'total_amount',
                label: 'Valor',
                getValue: (order) => order.total_amount || 0,
                render: (order) => (
                    <span className="text-black dark:text-white">{FormatPrice(order.total_amount)}</span>
                ),
            },
            {
                key: 'items',
                label: 'Itens',
                getValue: (order) => formatOrderItemsSummary(order),
                render: (order) => formatOrderItemsSummary(order),
            },
        ],
        [],
    );

    const shouldShowFullPageLoading =
        loading &&
        ((isDetailsMode && !selectedCustomer && orders.length === 0) || (!isDetailsMode && customers.length === 0));

    if (shouldShowFullPageLoading) {
        return (
            <div className="space-y-6">
                <PageHeader title={isDetailsMode ? 'Cliente' : 'Clientes'} subtitle="Carregando dados..." />
                <div className="rounded-xl border border-mono-200 bg-white p-8 text-center dark:border-mono-800 dark:bg-mono-950">
                    <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-black dark:border-white" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <PageHeader title={isDetailsMode ? 'Cliente' : 'Clientes'} subtitle="Erro ao carregar dados" />
                <ErrorCard
                    title="Falha ao carregar clientes"
                    message={error}
                    action="Tentar novamente"
                    actionFn={isDetailsMode ? loadCustomerDetails : loadCustomers}
                    type="error"
                />
            </div>
        );
    }

    if (isDetailsMode) {
        const customerName =
            selectedCustomer?.display_name ||
            selectedCustomer?.buyer_nickname ||
            `Cliente ${selectedCustomer?.buyer_id || buyerId}`;

        return (
            <div className="space-y-6">
                <PageHeader
                    title={customerName}
                    subtitle={`Historico de compras do cliente #${selectedCustomer?.buyer_id || buyerId}`}
                    actions={
                        <Button variant="secondary" onClick={() => navigate('/customers')}>
                            <ArrowLeft size={18} className="mr-2" />
                            Voltar
                        </Button>
                    }
                />

                <div className="grid gap-4 md:grid-cols-4">
                    <div className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                        <p className="text-xs text-mono-500 dark:text-mono-400">Nome</p>
                        <p className="mt-1 font-semibold text-black dark:text-white">{customerName}</p>
                    </div>
                    <div className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                        <p className="text-xs text-mono-500 dark:text-mono-400">Total de pedidos</p>
                        <p className="mt-1 font-semibold text-black dark:text-white">
                            {selectedCustomer?.total_orders || 0}
                        </p>
                    </div>
                    <div className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                        <p className="text-xs text-mono-500 dark:text-mono-400">Total gasto</p>
                        <p className="mt-1 font-semibold text-black dark:text-white">
                            {FormatPrice(selectedCustomer?.total_spent || 0)}
                        </p>
                    </div>
                    <div className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                        <p className="text-xs text-mono-500 dark:text-mono-400">Ultima compra</p>
                        <p className="mt-1 font-semibold text-black dark:text-white">
                            {FormatDateTimeBr(selectedCustomer?.last_order_date, { blank: '-' })}
                        </p>
                    </div>
                </div>

                <StandardTable
                    rows={orders}
                    columns={customerOrderColumns}
                    rowKey={(row) => row.id}
                    exportFileName={`cliente-${selectedCustomer?.buyer_id || buyerId}-pedidos`}
                    emptyMessage="Nenhum pedido encontrado para este cliente."
                    searchPlaceholder="Pesquisar pedidos do cliente"
                    searchMode="server"
                    paginationMode="server"
                    totalItems={ordersPaging.total}
                    currentPage={ordersPage}
                    onPageChange={setOrdersPage}
                    itemsPerPage={ordersPageSize}
                    onItemsPerPageChange={(nextPageSize) => {
                        setOrdersPageSize(nextPageSize);
                        setOrdersPage(1);
                    }}
                    pageSizeOptions={[15, 30, 50, 100, 200]}
                />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Clientes"
                subtitle="Lista de clientes com historico de compras"
                actions={
                    <Button onClick={loadCustomers}>
                        <ArrowClockwise size={18} className="mr-2" />
                        Atualizar
                    </Button>
                }
            />

            <StandardTable
                rows={customersWithFallbackName}
                columns={customerColumns}
                rowKey={(row) => row.buyer_id}
                exportFileName="clientes"
                emptyMessage="Nenhum cliente encontrado."
                searchPlaceholder="Buscar por nome, nickname, ID ou historico"
                searchMode="server"
                searchTerm={customersSearchInput}
                onSearchTermChange={setCustomersSearchInput}
                sortMode="server"
                sortState={customersSortState}
                onSortStateChange={setCustomersSortState}
                paginationMode="server"
                totalItems={customersPaging.total}
                currentPage={customersPage}
                onPageChange={setCustomersPage}
                itemsPerPage={customersPageSize}
                onItemsPerPageChange={(nextPageSize) => {
                    setCustomersPageSize(nextPageSize);
                    setCustomersPage(1);
                }}
                pageSizeOptions={[15, 30, 50, 100, 200]}
            />
        </div>
    );
};

export default Customers;
